import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import plantsData from '../data/plants.json';

export default function PlantDetails() {
  const { plantId } = useParams();
  const { currentUser } = useAuth();
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const plant = plantsData.find(p => p.plantId === parseInt(plantId));

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!plant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Plant Not Found</h1>
          <p className="text-gray-600">The plant you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star text-accent"></i>);
    }

    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt text-accent"></i>);
    }

    const remainingStars = 5 - stars.length;
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star text-accent"></i>);
    }

    return stars;
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    setBookingSuccess(true);
    setTimeout(() => {
      setBookingSuccess(false);
      e.target.reset();
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Plant Details */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="md:flex">
            <div className="md:w-1/2">
              <img
                src={plant.image}
                alt={plant.plantName}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>
            <div className="md:w-1/2 p-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {plant.plantName}
              </h1>
              
              <div className="flex items-center space-x-4 mb-4">
                <span className="text-2xl font-bold text-primary-600">
                  ${plant.price}
                </span>
                <div className="flex items-center space-x-1">
                  {renderStars(plant.rating)}
                  <span className="text-gray-600 ml-1">({plant.rating})</span>
                </div>
                <span className="text-gray-600">
                  {plant.availableStock} in stock
                </span>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                {plant.description}
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex">
                  <span className="font-medium text-gray-900 w-32">Care Level:</span>
                  <span className="text-gray-700">{plant.careLevel}</span>
                </div>
                <div className="flex">
                  <span className="font-medium text-gray-900 w-32">Category:</span>
                  <span className="text-gray-700">{plant.category}</span>
                </div>
                <div className="flex">
                  <span className="font-medium text-gray-900 w-32">Provider:</span>
                  <span className="text-gray-700">{plant.providerName}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Book a Consultation
          </h2>

          {bookingSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <i className="fas fa-check-circle text-green-500 mr-2"></i>
                <span className="text-green-800 font-medium">
                  Consultation booked successfully! Our expert will contact you soon.
                </span>
              </div>
            </div>
          )}

          <form onSubmit={handleBookingSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  defaultValue={currentUser.displayName || ''}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Email
                </label>
                <input
                  type="email"
                  defaultValue={currentUser.email}
                  className="input-field"
                  required
                />
              </div>
            </div>
            <button type="submit" className="btn-primary">
              Book Now
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}